import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select, Checkbox, SearchableSelect } from '../atoms';
import type { SelectOption } from '../atoms';
import type { GroupedSelectOption } from '../atoms/SearchableSelect';
import { PageHeader } from '../shell';
import { DataTable, Pagination, Modal, useToast } from '../organisms';
import { useUserNames } from '../lib/useUserNames';

/**
 * ConsultantDeals — /v2/consultant-deals
 *
 * A consultant deal ties a Consultant to a Client's Property and carries that property's
 * commission split (ATA part 2 §1 + §3): the four buckets Agent / Manager / Originator /
 * Commission, each up to two people, totalling exactly 100. The Consultant is an internal
 * user or a client contact, stored on the two nullable FKs (consultant_user_id /
 * consultant_id) exactly like the parcel Consultant, and offered from one grouped typeahead:
 * Internal Users first (the usual pick), then the client's contacts, then the property's.
 *
 * Every person renders as a full name, never an identifier.
 *
 * Resolution precedence when a fee is split: a property-scoped deal wins over a
 * client-scoped deal, which wins over a consultant-default deal (neither client nor property
 * set). New deals are always property-scoped; the older shapes remain readable.
 */

type SplitType = 'percent' | 'flat';

interface ConsultantDeal {
  id: string;
  consultant_id: string | null;
  consultant_user_id: string | null;
  client_id: string | null;
  property_id: string | null;
  split_type: SplitType | null;
  split_pct: number | string | null;
  flat_amount: number | string | null;
  rate: number | string | null;
  is_active: boolean | number | null;
  created_at: string | null;
}

interface DerivedSplit {
  rows: Array<{ role: string; role_label: string; slot: number; inherited: boolean; user_id: string | null; contact_id: string | null; assignee_name: string; percent: number }>;
  unallocated: Array<{ role: string; label: string; percent: number }>;
  total: number;
}
interface Contact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

interface NamedRow {
  id: string;
  name: string | null;
  client_id?: string | null;
}

function contactLabel(c: Contact): string {
  const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return full || c.email || '—';
}

function formatPct(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n}%`;
}

function formatMoney(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `$${Math.round(n).toLocaleString()}`;
}

function formatDate(v: string | null): string {
  if (!v) return '—';
  // created_at is a full ISO timestamp; the date portion is zone-stable enough
  // for a list column (avoids the spurious "12:00 AM" a Date render would add).
  const day = v.slice(0, 10);
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function payoutFor(deal: ConsultantDeal): string {
  if (deal.split_type === 'flat') return formatMoney(deal.flat_amount);
  return formatPct(deal.split_pct);
}

function scopeFor(deal: ConsultantDeal): { label: string; variant: 'info' | 'warning' | 'neutral' } {
  if (deal.property_id) return { label: 'Property', variant: 'info' };
  if (deal.client_id) return { label: 'Client', variant: 'warning' };
  return { label: 'Consultant default', variant: 'neutral' };
}

/** The deal's consultant as a picker value: `u:<userId>` or `c:<contactId>`. */
function consultantValue(deal: ConsultantDeal | null | undefined): string | null {
  if (!deal) return null;
  if (deal.consultant_user_id) return `u:${deal.consultant_user_id}`;
  if (deal.consultant_id) return `c:${deal.consultant_id}`;
  return null;
}

const PAGE_SIZE = 20;

export function ConsultantDeals() {
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [modalOpen, setModalOpen] = useState(false);
  const [editDeal, setEditDeal] = useState<ConsultantDeal | null>(null);
  // Date-range filter on the deal's created date.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const users = useUserNames();

  const dealsQuery = useQuery<ConsultantDeal[]>({
    queryKey: ['consultant-deals'],
    queryFn: ({ signal }) => api.get<ConsultantDeal[]>('/api/consultant-deals', signal),
  });

  // Names for the external consultants some deals still carry on consultant_id.
  const contactsQuery = useQuery<Contact[]>({
    queryKey: ['contacts'],
    queryFn: ({ signal }) => api.get<Contact[]>('/api/contacts', signal),
  });

  const clientsQuery = useQuery<NamedRow[]>({
    queryKey: ['clients-list'],
    queryFn: ({ signal }) => api.get<NamedRow[]>('/api/clients', signal),
  });

  const propertiesQuery = useQuery<NamedRow[]>({
    queryKey: ['properties-list'],
    queryFn: ({ signal }) => api.get<NamedRow[]>('/api/properties', signal),
  });

  const contactNames = useMemo(() => {
    const map = new Map<string, string>();
    (contactsQuery.data ?? []).forEach(c => map.set(c.id, contactLabel(c)));
    return map;
  }, [contactsQuery.data]);

  const clientNames = useMemo(() => {
    const map = new Map<string, string>();
    (clientsQuery.data ?? []).forEach(c => map.set(c.id, c.name ?? '—'));
    return map;
  }, [clientsQuery.data]);

  const propertyNames = useMemo(() => {
    const map = new Map<string, string>();
    (propertiesQuery.data ?? []).forEach(p => map.set(p.id, p.name ?? '—'));
    return map;
  }, [propertiesQuery.data]);

  const consultantName = (d: ConsultantDeal): string => {
    if (d.consultant_user_id) return users.nameOf(d.consultant_user_id);
    if (d.consultant_id) return contactNames.get(d.consultant_id) ?? '—';
    return '—';
  };

  const clientOptions = useMemo<SelectOption[]>(
    () => (clientsQuery.data ?? []).map(c => ({ value: c.id, label: c.name ?? '—' })),
    [clientsQuery.data],
  );

  const columns = useMemo<ColumnDef<ConsultantDeal>[]>(() => [
    {
      id: 'consultant',
      header: 'Consultant',
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{consultantName(row.original)}</span>
      ),
    },
    {
      id: 'scope',
      header: 'Scope',
      cell: ({ row }) => {
        const s = scopeFor(row.original);
        const target = row.original.property_id
          ? propertyNames.get(row.original.property_id)
          : row.original.client_id
            ? clientNames.get(row.original.client_id)
            : null;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Badge variant={s.variant}>{s.label}</Badge>
            {target && (
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{target}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'payout',
      header: 'Payout',
      cell: ({ row }) => {
        const d = row.original;
        const isFlat = d.split_type === 'flat';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color: 'var(--color-ink)' }}>{payoutFor(d)}</span>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              {isFlat ? 'Flat fee' : 'Percent split'}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'rate',
      header: 'Rate override',
      cell: ({ getValue }) => {
        const v = getValue() as number | string | null;
        return v === null || v === undefined || v === '' ? '—' : formatPct(v);
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ getValue }) => formatDate(getValue() as string | null),
    },
    {
      accessorKey: 'is_active',
      header: 'Active',
      cell: ({ getValue }) => {
        const active = Boolean(Number(getValue() ?? 0)) || getValue() === true;
        return <Badge variant={active ? 'success' : 'neutral'} dot>{active ? 'Active' : 'Inactive'}</Badge>;
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="secondary"
          onClick={(e) => { e.stopPropagation(); setEditDeal(row.original); }}
        >
          Edit
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [contactNames, clientNames, propertyNames, users.byId]);

  // No consultant-deal detail route exists, so a row opens the record the deal is
  // scoped to — following the same property > client > consultant-default
  // precedence the Scope column shows.
  const openDeal = (deal: ConsultantDeal) => {
    if (deal.property_id) navigate(`/properties/${deal.property_id}`);
    else if (deal.client_id) navigate(`/clients/${deal.client_id}`);
    else if (deal.consultant_id) navigate(`/contacts/${deal.consultant_id}`);
    else setEditDeal(deal);
  };

  const allDeals = dealsQuery.data ?? [];

  // Date-range filter (inclusive) on created_at.
  const deals = useMemo(() => {
    if (!dateFrom && !dateTo) return allDeals;
    return allDeals.filter(d => {
      const day = (d.created_at ?? '').slice(0, 10);
      if (!day) return false;
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    });
  }, [allDeals, dateFrom, dateTo]);

  const pageCount = Math.max(1, Math.ceil(deals.length / pageSize));
  const clampedPage = Math.min(pageIndex, pageCount - 1);
  const pagedDeals = useMemo(
    () => deals.slice(clampedPage * pageSize, (clampedPage + 1) * pageSize),
    [deals, clampedPage, pageSize],
  );

  const filtersActive = !!(dateFrom || dateTo);

  return (
    <>
      <PageHeader
        title="Consultant deals"
        subtitle={dealsQuery.isLoading ? 'Loading…' : `${deals.length} fee-split deal${deals.length === 1 ? '' : 's'}${filtersActive ? ` (of ${allDeals.length})` : ''}`}
        actions={
          <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <Button variant="secondary" size="md" onClick={() => navigate('/commissions')}>
              View commission ledger
            </Button>
            <Button variant="primary" size="md" onClick={() => setModalOpen(true)}>
              + New deal
            </Button>
          </div>
        }
      />

      <div style={{
        marginBottom: 'var(--sp-5)',
        padding: 'var(--sp-3) var(--sp-4)',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        color: 'var(--color-ink-3)',
        fontSize: 'var(--fs-sm)',
      }}>
        <strong style={{ color: 'var(--color-ink)' }}>Resolution precedence:</strong>{' '}
        a <Badge variant="info">Property</Badge> deal wins over a{' '}
        <Badge variant="warning">Client</Badge> deal, which wins over a{' '}
        <Badge variant="neutral">Consultant default</Badge> (neither client nor property set).
      </div>

      {/* Date-range filter on the deal's created date. */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 'var(--sp-3)',
        marginBottom: 'var(--sp-4)',
      }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', fontWeight: 'var(--fw-medium)' }}>Created from</span>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPageIndex(0); }}
            aria-label="Created from"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', fontWeight: 'var(--fw-medium)' }}>Created to</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPageIndex(0); }}
            aria-label="Created to"
          />
        </label>
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setPageIndex(0); }}>
            Clear
          </Button>
        )}
      </div>

      {dealsQuery.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Loading deals…</p>
      ) : dealsQuery.error ? (
        <p style={{ color: 'var(--color-status-red)' }}>Could not load consultant deals.</p>
      ) : deals.length === 0 ? (
        <div style={{
          padding: 'var(--sp-6)',
          textAlign: 'center',
          color: 'var(--color-ink-3)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)',
        }}>
          {filtersActive ? 'No deals were created in that date range.' : 'No consultant deals yet.'}
        </div>
      ) : (
        <>
          <DataTable columns={columns} data={pagedDeals} onRowClick={openDeal} />
          <Pagination
            pageIndex={clampedPage}
            pageCount={pageCount}
            pageSize={pageSize}
            totalRows={deals.length}
            onPageChange={setPageIndex}
            onPageSizeChange={(s) => { setPageSize(s); setPageIndex(0); }}
          />
        </>
      )}

      {(modalOpen || editDeal) && (
        <ConsultantDealModal
          deal={editDeal}
          onClose={() => { setModalOpen(false); setEditDeal(null); }}
          clientOptions={clientOptions}
          properties={propertiesQuery.data ?? []}
          users={users}
          contactNames={contactNames}
        />
      )}
    </>
  );
}

// ─── Create / edit modal ─────────────────────────────────────────────────────

interface ConsultantDealModalProps {
  deal: ConsultantDeal | null;
  onClose: () => void;
  clientOptions: SelectOption[];
  properties: NamedRow[];
  users: ReturnType<typeof useUserNames>;
  contactNames: Map<string, string>;
}

/**
 * Field order is the way ATA works: Client, then the Property under it, then who works it,
 * then how the fee is shared. Each step unlocks the next, and changing the client clears
 * everything below it — a property or a person from the old client cannot linger.
 */
function ConsultantDealModal({ deal, onClose, clientOptions, properties, users, contactNames }: ConsultantDealModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEdit = !!deal;

  const [clientId, setClientId] = useState<string | null>(deal?.client_id ?? null);
  const [propertyId, setPropertyId] = useState<string | null>(deal?.property_id ?? null);
  const [consultant, setConsultant] = useState<string | null>(consultantValue(deal));
  const [splitType, setSplitType] = useState<SplitType>(deal?.split_type ?? 'percent');
  const [flatAmount, setFlatAmount] = useState(deal?.flat_amount != null ? String(deal.flat_amount) : '');
  const [rate, setRate] = useState(deal?.rate != null ? String(deal.rate) : '');
  const [isActive, setIsActive] = useState(
    deal ? (Boolean(Number(deal.is_active ?? 0)) || deal.is_active === true) : true,
  );

  // People for the pickers: the client's contacts, the property's contacts.
  const clientContactsQuery = useQuery<Contact[]>({
    queryKey: ['contacts-for-client', clientId],
    queryFn: async ({ signal }) => {
      const resp = await api.get<Contact[] | { rows: Contact[] }>(`/api/contacts?client_id=${clientId}&limit=200`, signal);
      return Array.isArray(resp) ? resp : resp.rows;
    },
    enabled: !!clientId,
  });
  const propertyContactsQuery = useQuery<Contact[]>({
    queryKey: ['contacts-for-property', propertyId],
    queryFn: ({ signal }) => api.get<Contact[]>(`/api/contacts-by-entity/properties/${propertyId}`, signal),
    enabled: !!propertyId,
  });

  const propertyOptions = useMemo<SelectOption[]>(
    () => properties.filter(p => !clientId || p.client_id === clientId).map(p => ({ value: p.id, label: p.name ?? '—' })),
    [properties, clientId],
  );

  const peopleOptions = useMemo<GroupedSelectOption[]>(() => {
    const propertyContacts = propertyContactsQuery.data ?? [];
    const propertyIds = new Set(propertyContacts.map(c => c.id));
    // A contact on both the client and the property shows once, under Property Contacts.
    const clientContacts = (clientContactsQuery.data ?? []).filter(c => !propertyIds.has(c.id));
    const byName = (a: GroupedSelectOption, b: GroupedSelectOption) => a.label.localeCompare(b.label);
    return [
      ...users.options.map(o => ({ value: `u:${o.value}`, label: o.label, group: 'Internal Users' })),
      ...clientContacts.map(c => ({ value: `c:${c.id}`, label: contactLabel(c), group: 'Client Contacts' })).sort(byName),
      ...propertyContacts.map(c => ({ value: `c:${c.id}`, label: contactLabel(c), group: 'Property Contacts' })).sort(byName),
    ];
  }, [users.options, clientContactsQuery.data, propertyContactsQuery.data]);

  const consultantUserId = consultant?.startsWith('u:') ? consultant.slice(2) : null;
  const consultantContactId = consultant?.startsWith('c:') ? consultant.slice(2) : null;
  // The split is not typed here (ATA part 3 §3a): it derives from the property's assignments and
  // the firm's fixed role percents, and the deal mirrors it read-only.
  const derivedQuery = useQuery<DerivedSplit>({
    queryKey: ['commission-split-property', propertyId],
    queryFn: ({ signal }) => api.get<DerivedSplit>(`/api/commissions/splits/property/${propertyId}`, signal),
    enabled: !!propertyId,
  });

  const ready = !!clientId && !!propertyId && !!consultant;

  const num = (s: string): number | null => {
    const t = s.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const save = useMutation({
    mutationFn: async () => {
      // The legacy single percentage stays in step with the derived split: the consultant's own Agent share.
      const agentShare = (derivedQuery.data?.rows ?? []).filter(r => r.role === 'agent' && ((consultantUserId && r.user_id === consultantUserId) || (consultantContactId && r.contact_id === consultantContactId)))
        .reduce((a, r) => a + Number(r.percent), 0);
      const body = {
        consultant_user_id: consultantUserId,
        consultant_id: consultantContactId,
        client_id: clientId,
        property_id: propertyId,
        split_type: splitType,
        split_pct: splitType === 'percent' ? (agentShare || null) : null,
        flat_amount: splitType === 'flat' ? num(flatAmount) : null,
        rate: num(rate),
        is_active: isActive,
      };
      if (isEdit) await api.put<ConsultantDeal>(`/api/consultant-deals/${deal!.id}`, body);
      else await api.post<ConsultantDeal>('/api/consultant-deals', body);
    },
    onSuccess: () => {
      toast({ variant: 'success', message: isEdit ? 'Deal updated.' : 'Deal created.' });
      queryClient.invalidateQueries({ queryKey: ['consultant-deals'] });
      onClose();
    },
    onError: (e) => {
      const b = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: b?.error || (e as Error).message });
    },
  });

  const consultantLabel = consultant
    ? (consultantUserId ? users.nameOf(consultantUserId) : contactNames.get(consultantContactId!) ?? null)
    : null;

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Edit consultant deal' : 'New consultant deal'} size="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <Field label="Client" required>
          <SearchableSelect
            aria-label="Client"
            options={clientOptions}
            value={clientId}
            onChange={(v) => {
              setClientId(v);
              // Everything below depends on the client: a property or a person from another client cannot linger.
              setPropertyId(null); setConsultant(null);
            }}
            placeholder="Search clients…"
          />
        </Field>

        <Field label="Property" required>
          <SearchableSelect
            aria-label="Property"
            options={propertyOptions}
            value={propertyId}
            onChange={setPropertyId}
            placeholder={clientId ? 'Search this client’s properties…' : 'Choose a client first'}
            disabled={!clientId}
          />
        </Field>

        <Field label="Consultant" required>
          <SearchableSelect
            aria-label="Consultant"
            options={peopleOptions}
            value={consultant}
            onChange={setConsultant}
            placeholder={clientId ? 'Search people…' : 'Choose a client first'}
            disabled={!clientId}
          />
          {consultantLabel === null && consultant && (
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>This person is no longer on the client. Pick again to change.</span>
          )}
        </Field>

        <Field label="Commission split">
          <div data-testid="commission-split" style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', display: 'grid', gap: 'var(--sp-1)' }}>
            {!propertyId ? (
              <span style={{ color: 'var(--color-ink-3)' }}>Choose a property: the split follows its assignments and the firm's fixed role percents.</span>
            ) : derivedQuery.isLoading ? (
              <span style={{ color: 'var(--color-ink-3)' }}>Loading split…</span>
            ) : derivedQuery.error || !derivedQuery.data || !Array.isArray(derivedQuery.data.rows) ? (
              <span style={{ color: 'var(--color-status-red)' }}>Couldn’t load the property’s split.</span>
            ) : (
              <>
                {derivedQuery.data.rows.map((r, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
                    <span>{r.role_label}{derivedQuery.data!.rows.filter((x) => x.role === r.role).length > 1 ? ` slot ${r.slot}` : ''}: <strong>{r.assignee_name}</strong>{r.inherited ? <span style={{ color: 'var(--color-ink-3)' }}> (from the client team)</span> : null}</span>
                    <span>{r.percent}%</span>
                  </div>
                ))}
                {(derivedQuery.data.unallocated ?? []).map((u) => (
                  <div key={u.role} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', color: 'var(--color-status-red)' }}>
                    <span>{u.label}: nobody assigned</span><span>{u.percent}% unallocated</span>
                  </div>
                ))}
                <div data-testid="commission-split-total" style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--sp-1)', fontWeight: 'var(--fw-medium)' }}>
                  <span>Total</span><span>{derivedQuery.data.total}%</span>
                </div>
                <span style={{ color: 'var(--color-ink-3)' }}>Percents are fixed by role in Org Settings; change the people on the property’s Assignments card.</span>
              </>
            )}
          </div>
        </Field>

        <Field label="Payout type">
          <Select
            aria-label="Payout type"
            value={splitType}
            onChange={(e) => setSplitType(e.target.value as SplitType)}
            style={{ width: '100%' }}
            options={[
              { value: 'percent', label: 'Percentage split (% of fee)' },
              { value: 'flat', label: 'Flat fee ($)' },
            ]}
          />
        </Field>

        {splitType === 'flat' && (
          <Field label="Flat fee ($, optional)">
            <Input
              type="number"
              aria-label="Flat amount"
              value={flatAmount}
              onChange={(e) => setFlatAmount(e.target.value)}
              placeholder="e.g. 1500"
              min={0}
              style={{ width: '100%' }}
            />
          </Field>
        )}

        <Field label="Rate override (%, optional)">
          <Input
            type="number"
            aria-label="Rate override"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="Consultant-tier contingency rate"
            style={{ width: '100%' }}
          />
        </Field>

        <Checkbox
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          label="Active"
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button variant="primary" size="sm" loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            {isEdit ? 'Save changes' : 'Create deal'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', fontWeight: 'var(--fw-medium)' }}>
        {label}
        {required && <span aria-hidden="true" style={{ color: 'var(--color-status-red)', marginLeft: 2 }}>*</span>}
      </span>
      {children}
    </div>
  );
}
